import { Grid, Autocomplete, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { formatDateOnlyDMY } from "helpers";
import { Article, Client, Store, Location, HistoryFiltersInterface } from "types";

export interface HistoryFiltersProps {
  viewMode: "detalle" | "resumido";

  filters: HistoryFiltersInterface;
  setFilters: React.Dispatch<React.SetStateAction<HistoryFiltersInterface>>;

  selectedClients: Client[];
  setSelectedClients: (c: Client[]) => void;

  uniqueValues: {
    lotes?: string[];
    ocs?: string[];
    vencimientos?: string[];
    ingresos?: string[];
    movimientoFechas?: string[];
  };


  filteredArticles: Article[];

  availableStores: Store[];
  availableLocations: Location[];

  filterVisibility: {
    show_lote: boolean;
    show_oc: boolean;
    show_fecha_vencimiento: boolean;
  };

  clients: Client[];
  stores: Store[];
  uniqueArticles: Article[];
  loading: boolean;
}

export default function HistoryFilters({
  viewMode,
  filters,
  setFilters,
  selectedClients,
  setSelectedClients,
  uniqueValues,
  filteredArticles,
  availableStores,
  availableLocations,
  filterVisibility,
  clients,
  stores,
  uniqueArticles,
  loading
}: HistoryFiltersProps) {
  return (
    <Grid container columnSpacing={2} rowSpacing={1} my={1}>
      <Grid item md={3} sm={6} xs={12}>
        <DatePicker
          label="Fecha Desde"
          value={filters.fecha_desde}
          onChange={(newValue) =>
            setFilters(prev => ({
              ...prev,
              fecha_desde: newValue
            }))
          }
          disabled={loading}
          slotProps={{
            textField: {
              fullWidth: true,
            },
          }}
        />
      </Grid>

      <Grid item md={3} sm={6} xs={12}> 
        <DatePicker
          label="Fecha Hasta"
          value={filters.fecha_hasta}
          onChange={(newValue) =>
            setFilters(prev => ({
              ...prev,
              fecha_hasta: newValue
            }))
          }
          disabled={loading}
          slotProps={{
            textField: {
              fullWidth: true,
            },
          }}
        />
      </Grid>

      <Grid item md={3} sm={6} xs={12}>
        <Autocomplete
          multiple
          options={clients.filter((c) => c.parent_client_id == null).map((c) => ({
            ...c,
            id: c.id,
            label: `${c.code} - ${c.razon_social}`,
          }))}
          onChange={(_, value) => {
            setSelectedClients(value);
            setFilters((prev: any) => ({
              ...prev,
              clientIds: value.map((v) => v.id),
              articleId: undefined,
              locationId: undefined,
            }));
          }}
          renderInput={(params) => <TextField {...params} label="Cliente" />}
          disabled={loading}
        />
      </Grid>

      {viewMode === "resumido" && (
        <Grid item md={3} sm={6} xs={12}>
          <Autocomplete
            options={uniqueArticles}
            getOptionLabel={(o) => o.code ?? ""}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, value) =>
              setFilters((prev: any) => ({
                ...prev,
                articleId: value?.id,
                storeIds: [],
                locationIds: [],
                locationFrom: undefined,
                locationTo: undefined,
              }))
            }
            renderInput={(params) => <TextField {...params} label="Artículo" />}
            disabled={loading}
          />
        </Grid>
      )}

      <Grid item md={3} sm={6} xs={12}>
        <Autocomplete
          multiple
          options={
            viewMode === "detalle"
              ? [{ id: 0, label: "Todos" }, ...stores.map((s) => ({ id: s.id, label: s.description }))]
              : availableStores.map((s) => ({ id: s.id, label: s.description }))
          }
          onChange={(_, value) => {
            const ids = value.some((v) => v.id === 0)
              ? stores.map((s) => s.id)
              : value.map((v) => v.id);

            setFilters((prev: any) => ({
              ...prev,
              storeIds: ids,
              locationId: undefined,
            }));
          }}
          renderInput={(params) => <TextField {...params} label="Depósitos" />}
          disabled={loading}
        />
      </Grid>

      <Grid item md={3} sm={6} xs={12}>
        <Autocomplete
          disabled={loading || !filters.storeIds.length}
          options={availableLocations
            .map((l) => ({ id: l.id, label: l.code }))
          }
          value={
            filters.locationId
              ? {
                  id: filters.locationId,
                  label:
                    availableLocations.find((l) => l.id === filters.locationId)
                      ?.code || "",
                }
              : null 
          }
          onChange={(_, value) =>
            setFilters(prev => ({
              ...prev,
              locationId: value?.id ?? undefined
            }))
          }
          renderInput={(params) => (
            <TextField {...params} label="Ubicación" />
          )}
        />
      </Grid>

      {viewMode === "detalle" && (
        <Grid item md={3} sm={6} xs={12}>
          <Autocomplete
            options={filteredArticles}
            getOptionLabel={(o) => o.code ?? ""}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, value) =>
              setFilters((prev: any) => ({
                ...prev,
                articleId: value?.id,
              }))
            }
            renderInput={(params) => <TextField {...params} label="Artículo" />}
            disabled={loading}
          />
        </Grid>
      )}

      <Grid item md={3} sm={6} xs={12}>
        <TextField
          label="Descripción de Artículo"
          type="text"
          fullWidth
          defaultValue={filters.articleDescription}
          onBlur={(e) =>
            setFilters((prev) => ({
              ...prev,
              articleDescription: e.target.value.trim(),
            }))
          }
          disabled={loading}
        />
      </Grid>

      {filterVisibility.show_lote && uniqueValues.lotes?.length ? (
        <Grid item md={3} sm={6} xs={12}>
          <Autocomplete
            options={uniqueValues.lotes.map((v) => ({ label: v }))}
            onChange={(_, v) =>
              setFilters((prev: any) => ({ ...prev, lote: v?.label ?? "" }))
            }
            renderInput={(params) => <TextField {...params} label="Lote" />}
            disabled={loading}
          />
        </Grid>
      ) : null}


      {filterVisibility.show_oc && uniqueValues.ocs?.length ? (
        <Grid item md={3} sm={6} xs={12}>
          <Autocomplete
            options={uniqueValues.ocs.map((v) => ({ label: v }))}
            onChange={(_, v) =>
              setFilters((prev: any) => ({ ...prev, oc: v?.label ?? "" }))
            }
            renderInput={(params) => <TextField {...params} label="OC" />}
            disabled={loading}
          />
        </Grid>
      ) : null}


      {filterVisibility.show_fecha_vencimiento && uniqueValues.vencimientos?.length ? (
        <Grid item md={3} sm={6} xs={12}>
          <Autocomplete
            options={uniqueValues.vencimientos}
            getOptionLabel={(opt) => formatDateOnlyDMY(opt)}
            onChange={(_, value) =>
              setFilters((f) => ({ ...f, fecha_vencimiento: value ?? "" }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Fecha de Vencimiento" />
            )}
            disabled={loading}
          />
        </Grid>
      ) : null}


      {uniqueValues.ingresos?.length ? (
        <Grid item md={3} sm={6} xs={12}>
          <Autocomplete
            options={uniqueValues.ingresos}
            getOptionLabel={(opt) => formatDateOnlyDMY(opt)}
            onChange={(_, value) =>
              setFilters((f) => ({ ...f, fecha_ingreso: value ?? "" }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Fecha de Ingreso" />
            )}
            disabled={loading}
          />
        </Grid>
      ) : null}

    </Grid>
  );
}
